import { capture, initPostHog } from "./posthog";

import type { ProductBaseProperties } from "./product";
import { trackProductEvent } from "./product";

export type OnboardingEventName =
  | "onboarding_started"
  | "onboarding_completed"
  | "onboarding_intro_viewed"
  | "onboarding_intro_dismissed"
  | "onboarding_intro_completed"
  | "onboarding_intro_join_terreiro_clicked"
  | "onboarding_intro_create_terreiro_clicked"
  | "onboarding_terreiro_completeness_recalculated"
  | "onboarding_profile_completeness_recalculated"
  | "onboarding_medium_added"
  | "onboarding_activity_model_use_clicked"
  | "onboarding_activity_model_used_personal"
  | "onboarding_activity_model_used_terreiro"
  | "onboarding_event_model_use_clicked"
  | "onboarding_event_model_used_terreiro"
  | "onboarding_inventory_item_created"
  | "onboarding_income_created"
  | "onboarding_outcome_created"
  | "onboarding_monthly_fee_edited"
  | "onboarding_bank_account_created"
  | "onboarding_ritual_bath_created"
  | "onboarding_profile_switched_to_terreiro"
  | "onboarding_profile_switched_to_personal"
  | "onboarding_invite_generated"
  | "onboarding_invite_link_share_clicked"
  | "onboarding_invite_link_shared"
  | "onboarding_medium_invite_link_shared";

export type OnboardingProfileType = "personal" | "terreiro";
export type OnboardingTriggerType = "ui" | "mutation";

const ONBOARDING_ANALYTICS_ENABLED =
  (import.meta.env?.VITE_POSTHOG_ONBOARDING_ENABLED as string | undefined) !==
  "false";

export type OnboardingBaseProperties = {
  sourcePage: string;
  activeProfileType?: OnboardingProfileType;
  terreiroId?: string;
  mediumId?: string;
  success?: boolean;
  errorCode?: string | null;
  trigger?: OnboardingTriggerType;
  featureLimitBlocked?: boolean;
};

const MAX_ERROR_CODE_LENGTH = 120;

const sanitizeErrorCode = (value: unknown): string | null => {
  if (!value) return null;

  const raw =
    typeof value === "string"
      ? value
      : value instanceof Error
        ? value.message
        : String(value);

  const compact = raw.trim().replace(/\s+/g, "_").toLowerCase();
  if (!compact) return null;

  return compact.slice(0, MAX_ERROR_CODE_LENGTH);
};

const getActiveProfileType = (
  activeProfileType: OnboardingProfileType | undefined,
  terreiroId: string | undefined,
): OnboardingProfileType => {
  if (activeProfileType) return activeProfileType;
  return terreiroId ? "terreiro" : "personal";
};

const buildBaseProperties = (
  props: OnboardingBaseProperties,
): Record<string, unknown> => ({
  flow: "onboarding",
  onboarding_event_version: 1,
  ts_iso: new Date().toISOString(),
  source_page: props.sourcePage,
  active_profile_type: getActiveProfileType(
    props.activeProfileType,
    props.terreiroId,
  ),
  terreiro_id: props.terreiroId ?? null,
  medium_id: props.mediumId ?? null,
  success: props.success ?? true,
  error_code: props.errorCode ?? null,
  trigger: props.trigger ?? "ui",
  feature_limit_blocked: props.featureLimitBlocked ?? false,
});

export const toMilestoneBucket = (
  score: number | null | undefined,
): 25 | 50 | 75 | 100 | null => {
  if (typeof score !== "number" || Number.isNaN(score)) return null;
  if (score >= 100) return 100;
  if (score >= 75) return 75;
  if (score >= 50) return 50;
  if (score >= 25) return 25;
  return null;
};

export const toAmountBucket = (
  amount: number | null | undefined,
): string | null => {
  if (typeof amount !== "number" || Number.isNaN(amount) || amount < 0) {
    return null;
  }

  if (amount === 0) return "0";
  if (amount <= 50) return "1-50";
  if (amount <= 100) return "51-100";
  if (amount <= 250) return "101-250";
  if (amount <= 500) return "251-500";
  if (amount <= 1000) return "501-1000";
  return "1000+";
};

export const trackOnboardingEvent = (
  event: OnboardingEventName,
  baseProperties: OnboardingBaseProperties,
  eventProperties?: Record<string, unknown>,
): void => {
  if (!ONBOARDING_ANALYTICS_ENABLED) return;

  initPostHog();
  capture(event, {
    ...buildBaseProperties(baseProperties),
    ...(eventProperties ?? {}),
  });
};

export const trackOnboardingError = (
  event: OnboardingEventName,
  baseProperties: Omit<OnboardingBaseProperties, "success" | "errorCode">,
  error: unknown,
  eventProperties?: Record<string, unknown>,
): void => {
  trackOnboardingEvent(
    event,
    {
      ...baseProperties,
      success: false,
      errorCode: sanitizeErrorCode(error),
    },
    eventProperties,
  );
};

export type OnboardingLifecycleEventName =
  | "onboarding_started"
  | "onboarding_completed";

export const trackOnboardingProductEvent = (
  event: OnboardingLifecycleEventName,
  baseProperties: ProductBaseProperties,
  eventProperties?: Record<string, unknown>,
): void => {
  if (!ONBOARDING_ANALYTICS_ENABLED) return;

  trackProductEvent(event, baseProperties, eventProperties, "onboarding");
};
